import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config';
import { formatCurrency } from '../utils/formatUtils';

function getAuthHeaders(extra = {}) {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : { ...extra };
}

function resolveImageSrc(rawUrl) {
  const value = String(rawUrl || '').trim();
  if (!value) return '';
  if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:')) return value;
  if (value.startsWith('/static/') || value.startsWith('/assets/') || value.startsWith('/images/')) return value;
  if (value.startsWith('/uploads/') || value.startsWith('/api/uploads/')) return `${API_BASE_URL}${value}`;
  if (value.startsWith('uploads/')) return `${API_BASE_URL}/${value}`;
  if (value.startsWith('/')) return value;
  return `${API_BASE_URL}/${value}`;
}

function trendPill(item) {
  const direction = String(item?.priceTrendDirection || 'unknown');
  const pct = Number(item?.priceChange30dPercent);
  if (!Number.isFinite(pct)) {
    return {
      text: '30g trend yok',
      className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
    };
  }
  if (direction === 'up') {
    return {
      text: `%${Math.abs(pct).toFixed(1)} artis`,
      className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
    };
  }
  if (direction === 'down') {
    return {
      text: `%${Math.abs(pct).toFixed(1)} dusus`,
      className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    };
  }
  return {
    text: 'Stabil',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
  };
}

function RecipeRow({ item, primary = false }) {
  const pill = trendPill(item);
  const deltaTl = Number(item?.favoriteSincePriceDeltaTl);
  const hasDelta = Number.isFinite(deltaTl) && Math.abs(deltaTl) >= 0.01;
  const [imageBroken, setImageBroken] = useState(false);
  const deltaText = hasDelta
    ? `Favoriye eklediginden beri ${deltaTl > 0 ? '+' : ''}${formatCurrency(deltaTl)}`
    : 'Favoriden bugune fiyat farki icin yeterli veri yok';
  const resolvedImage = !imageBroken ? resolveImageSrc(item?.imageUrl || item?.image_url || '') : '';

  return (
    <div
      className={`rounded-2xl border p-4 flex gap-4 items-start ${primary
          ? 'border-primary/40 bg-primary/5 dark:bg-primary/10 shadow-lg shadow-primary/10'
          : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922]'
        }`}
    >
      <div className="size-14 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
        {resolvedImage ? (
          <img src={resolvedImage} alt={item?.title || 'Tarif'} className="w-full h-full object-cover" onError={() => setImageBroken(true)} />
        ) : (
          <span className="text-xl">🍽️</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-semibold text-slate-900 dark:text-white truncate">{item?.title || 'Tarif'}</h3>
          {primary ? (
            <span className="text-[10px] font-black px-2 py-1 rounded-full bg-primary text-white uppercase tracking-wider">
              One Cikan Oneri
            </span>
          ) : null}
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${pill.className}`}>{pill.text}</span>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{item?.priceTrendSummary || 'Trend verisi yok'}</p>
        <div className={`mt-2 text-xs ${hasDelta ? 'text-slate-600 dark:text-slate-300' : 'text-slate-500 dark:text-slate-400'}`}>
          {deltaText}
        </div>
      </div>
    </div>
  );
}

function mealChipStyle(category) {
  const c = String(category || '').toUpperCase();
  if (c === 'SOUP') return { emoji: '🍲', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' };
  if (c === 'SALAD') return { emoji: '🥗', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' };
  if (c === 'DESSERT' || c === 'PASTRY') return { emoji: '🍰', className: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-200' };
  if (c === 'BREAKFAST') return { emoji: '🍳', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200' };
  if (['MEAT', 'CHICKEN', 'SEAFOOD', 'MAIN', 'LEGUME', 'PASTA'].includes(c)) {
    return { emoji: '🍖', className: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200' };
  }
  return { emoji: '🍽️', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' };
}

function RecentMealsSection({ days, loading }) {
  const daysWithMeals = Array.isArray(days) ? days.filter((d) => d.meals && d.meals.length > 0) : [];
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">Son 7 Gün — Ne Yedin?</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Gerçekten pişirip tükettiğin öğünler gün gün. Her gün kayar: en eski gün düşer, yeni gün eklenir.
        </p>
      </div>
      {loading ? (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922] p-6 text-sm text-slate-500 dark:text-slate-300">
          Yukleniyor...
        </div>
      ) : daysWithMeals.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 p-6 text-sm text-slate-500 dark:text-slate-300">
          Son 7 günde kaydedilmiş öğün yok. Bir menüyü "pişirdim" olarak işaretledikçe burada gün gün birikecek.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {days.map((day) => {
            const hasMeals = day.meals && day.meals.length > 0;
            return (
              <div
                key={day.date}
                className={`rounded-2xl border p-4 ${day.isToday
                    ? 'border-primary/40 bg-primary/5 dark:bg-primary/10'
                    : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922]'
                  } ${!hasMeals ? 'opacity-60' : ''}`}
              >
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <div className="flex items-baseline gap-2">
                    <span className="font-bold text-slate-900 dark:text-white">{day.weekdayLabel}</span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">{day.dateLabel}</span>
                  </div>
                  {day.isToday ? (
                    <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-primary text-white uppercase tracking-wider">Bugün</span>
                  ) : null}
                </div>
                {hasMeals ? (
                  <div className="space-y-2">
                    {day.meals.map((meal) => (
                      <div key={`${day.date}-${meal.slot}`} className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 w-14 shrink-0 uppercase tracking-wide">
                          {meal.slotLabel}
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {meal.dishes.map((dish, di) => {
                            const chip = mealChipStyle(dish.category);
                            return (
                              <span
                                key={`${day.date}-${meal.slot}-${di}`}
                                className={`text-xs font-semibold px-2.5 py-1 rounded-full ${chip.className}`}
                              >
                                {chip.emoji} {dish.name}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 dark:text-slate-500 italic">Bu gün kayıt yok</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function Section({ title, subtitle, items, loading, maxItems = 3 }) {
  const limited = Array.isArray(items) ? items.slice(0, Math.max(0, maxItems)) : [];
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">{title}</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>
      </div>
      {loading ? (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922] p-6 text-sm text-slate-500 dark:text-slate-300">
          Yukleniyor...
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 p-6 text-sm text-slate-500 dark:text-slate-300">
          Henuz veri yok.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {limited.map((item) => (
            <RecipeRow key={`${item.entityId}-${item.id || item.title}`} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function MyFavorites() {
  const [loading, setLoading] = useState(true);
  const [frequent, setFrequent] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [error, setError] = useState('');
  const [recentDays, setRecentDays] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const resp = await fetch(`${API_BASE_URL}/api/favorites/recipes-dashboard?limit=24`, {
          headers: getAuthHeaders({ Accept: 'application/json' }),
          credentials: 'include'
        });
        if (!resp.ok) {
          throw new Error(`favorites_dashboard_http_${resp.status}`);
        }
        const data = await resp.json();
        if (!mounted) return;
        setFrequent(Array.isArray(data?.frequentlyUsedRecipes) ? data.frequentlyUsedRecipes : []);
        setFavorites(Array.isArray(data?.favoriteRecipes) ? data.favoriteRecipes : []);
      } catch (err) {
        if (!mounted) return;
        setError('Favori verileri yuklenemedi.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setRecentLoading(true);
        const resp = await fetch(`${API_BASE_URL}/api/consumptions/recent-meals`, {
          headers: getAuthHeaders({ Accept: 'application/json' }),
          credentials: 'include'
        });
        if (!resp.ok) throw new Error(`recent_meals_http_${resp.status}`);
        const data = await resp.json();
        if (!mounted) return;
        setRecentDays(Array.isArray(data?.days) ? data.days : []);
      } catch (err) {
        if (!mounted) return;
        setRecentDays([]);
      } finally {
        if (mounted) setRecentLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const recommendationSurface = useMemo(() => {
    const seen = new Set();
    const merged = [...(frequent || []), ...(favorites || [])].filter((item) => {
      const key = `${item?.entityId || ''}|${item?.title || ''}`;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return merged.slice(0, 3);
  }, [frequent, favorites]);

  const primaryRecommendation = recommendationSurface[0] || null;
  const hasAny = useMemo(() => frequent.length > 0 || favorites.length > 0, [frequent.length, favorites.length]);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922] p-5 md:p-6">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Favorilerim</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          Son 30 gunde tarif maliyet trendiyle birlikte sik kullandigin ve favori ekledigin tarifler.
        </p>
      </div>

      <RecentMealsSection days={recentDays} loading={recentLoading} />

      <section className="space-y-3">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">Sana Ozel Oneriler</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Karar kolayligi icin bu alanda en fazla 3 oneriden 1 tanesi one cikarilir.
          </p>
        </div>
        {loading ? (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922] p-6 text-sm text-slate-500 dark:text-slate-300">
            Yukleniyor...
          </div>
        ) : primaryRecommendation ? (
          <div className="space-y-3">
            <RecipeRow item={primaryRecommendation} primary />
            {recommendationSurface.slice(1).map((item) => (
              <RecipeRow key={`rec_${item.entityId}-${item.id || item.title}`} item={item} />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 p-6 text-sm text-slate-500 dark:text-slate-300">
            Oneri olusturmak icin yeterli veri yok.
          </div>
        )}
      </section>

      {error ? (
        <div className="rounded-2xl border border-rose-200 dark:border-rose-900/40 bg-rose-50 dark:bg-rose-950/20 p-4 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      <Section
        title="Sık Kullandığın Tarifler"
        subtitle="Favoriden bağımsız, gerçek tüketim davranışına göre siralanır."
        items={frequent}
        loading={loading}
      />

      <Section
        title="Favori Eklediklerin"
        subtitle="Kalp ve sabitleme sinyallerine göre oluşan favori listen."
        items={favorites}
        loading={loading}
      />

      {!loading && !error && !hasAny ? (
        <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/30 p-6 text-sm text-slate-500 dark:text-slate-300">
          Tarif kartlarindan favori ekledikce ve tarif kullandikca bu alan otomatik dolacak.
        </div>
      ) : null}
    </div>
  );
}
